import {
  extractVariables,
  variableLabel,
  type WabaTemplateMedia,
} from "@school-sales-ai/shared";

/**
 * The template as the recipient will see it, with the examples substituted.
 *
 * Worth the space: the admin is writing a message they cannot send to
 * themselves to check, and Meta's review is not a preview. A variable with no
 * example yet renders as a labelled chip rather than raw braces — this is where
 * `{{1}}` was most visible, and a chip reads as "something goes here".
 */
function renderBody(body: string, examples: string[]): React.ReactNode[] {
  const byName = new Map(extractVariables(body).map((name, i) => [name, examples[i] ?? ""]));

  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  for (const match of body.matchAll(/\{\{\s*([^{}]+?)\s*\}\}/g)) {
    const start = match.index;
    const name = match[1];
    if (start === undefined || name === undefined) continue;
    if (start > cursor) nodes.push(body.slice(cursor, start));
    cursor = start + match[0].length;

    const example = byName.get(name);
    if (example !== undefined && example.trim() !== "") {
      nodes.push(example);
    } else {
      nodes.push(
        <span key={`v${String(key++)}`} className="rounded bg-lime-pale px-1 text-ink-700">
          {/^\d+$/.test(name) ? `Dato ${name}` : variableLabel(name)}
        </span>,
      );
    }
  }
  if (cursor < body.length) nodes.push(body.slice(cursor));
  return nodes;
}

export function TemplatePreview({
  header,
  headerMedia = null,
  body,
  footer,
  examples,
}: {
  /** The TEXT header. Null on a media header. */
  header: string | null;
  /**
   * The media header's file, when there is one. Optional so all four call sites
   * kept compiling when media headers landed — the same reason the wire shape
   * uses sibling fields instead of a union on `header`.
   */
  headerMedia?: WabaTemplateMedia | null;
  body: string;
  footer: string | null;
  examples: string[];
}): React.JSX.Element {
  return (
    <div className="rounded-lg bg-[#e5ddd5] p-4">
      <div className="max-w-sm rounded-lg bg-white px-3 py-2 shadow-sm">
        {headerMedia !== null ? (
          headerMedia.kind === "image" && headerMedia.previewUrl ? (
            <img
              src={headerMedia.previewUrl}
              alt="Encabezado"
              className="mb-2 max-h-40 w-full rounded-md object-cover"
            />
          ) : (
            // WhatsApp shows a PDF as a file row with its name, so the preview
            // does too — and the name is the thing the admin can still change.
            <div className="mb-2 flex items-center gap-2 rounded-md bg-ink-050 px-2 py-2">
              <span className="text-lg" aria-hidden>
                📄
              </span>
              <span className="min-w-0 truncate text-xs text-ink-700" title={headerMedia.fileName}>
                {headerMedia.fileName}
              </span>
            </div>
          )
        ) : null}
        {header ? (
          <p className="mb-1 text-sm font-semibold text-ink-900">{header}</p>
        ) : null}
        <p className="whitespace-pre-wrap text-sm text-ink-800">
          {body === "" ? "…" : renderBody(body, examples)}
        </p>
        {footer ? <p className="mt-1 text-xs text-ink-500">{footer}</p> : null}
      </div>
    </div>
  );
}
